import {
    Anchor,
    Breadcrumbs,
    Group,
    Modal,
    Stack,
} from '@mantine/core'
import IonIcon from '@reacticons/ionicons'
import { getFacility } from 'app/state/ducks/facilities/selectors'
import { loadAllFacilities, loadOneFacility, loadOneFacilityAmounts, setOneFacility } from 'app/state/ducks/facilities/thunks'
import { loadCalendars } from 'app/state/ducks/holiday-calendars/thunks'
import { loadAllEntityProfiles } from 'app/state/ducks/entity-profile/thunks'
import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { UpdatedFacilitiesScreen } from '../facilities/updated-facilities-screen'
import { loadAllDeals, setOneDeal } from 'app/state/ducks/deals/thunks'
import { getDeals } from 'app/state/ducks/deals/selectors'
import {
    loadLendersPositionsByFacility,
    loadLendersPositionsByLoan,
} from 'app/state/ducks/lenders-positions/thunks'
import { LendersPosition, LendersPositionWithEntityNames } from 'app/models/lenders-position-params'
import {
    getFacilityLendersPositionsWithLoading,
    getLoanLendersPositionsWithLoading,
} from 'app/state/ducks/lenders-positions/selectors'
import { loadContacts } from 'app/state/ducks/contacts/thunks'
import { loadWireInstructions } from 'app/state/ducks/wire-instructions/thunks'
import { DealParams } from 'app/models/deal-params'
import { loadOneLoan } from 'app/state/ducks/loans/thunks'
import { getLoan } from 'app/state/ducks/loans/selectors'
import { loadOneLoanAmendment } from 'app/state/ducks/loan-amendments/thunks'
import { getLoanAmendment } from 'app/state/ducks/loan-amendments/selectors'
import { useListState } from '@mantine/hooks'
import { loadIndexRateOptions } from 'app/state/ducks/index-rate-options/thunks'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { stringToDate, highlightToday, formatDateToShow } from 'app/utils/util-functions'
import LenderAllocationTable from './funds-table'
import FundManagerLenderTable from './fund-managers-table'
import LenderAllocationsHeader from './lender-allocations-header'
import SegmentControlUpdated from 'app/views/components/segments/segment-control-updated'
import PrimaryInput from 'app/views/components/buttons/PrimaryInput'
import { loadAllFacilityAmendments } from 'app/state/ducks/facility-amendments/thunks'
import { getFacilityAmendments } from 'app/state/ducks/facility-amendments/selectors'
import { FacilityParams } from 'app/models/facility-params'
import MultiSelectSearchInput from 'app/views/components/inputs/selects/multi-select-search-input'
import { DateInput } from '@mantine/dates'
import { getEntities } from 'app/state/ducks/entities/selectors'

type LenderAllocationsContext = {
    effectiveDate: Date
    currency: string
    lendersPositions: LendersPositionWithEntityNames[]
    loading: boolean
    contractType: string | undefined
    status: string | undefined
    isAmendment: boolean
}

export const useLenderAllocationsContext = () =>
    useOutletContext<LenderAllocationsContext>()


export function LenderAllocationsDrillPage() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const { contractId, status, contractType } = useParams()
    const isFundManagerTab = location.pathname.endsWith('/fund-manager')
    const [searchTerms, setSearchTerms] = useState<string[]>([])

    const [openEdit, setOpenEdit] = useState<boolean>(false)

    const [effectiveDate, setEffectiveDate] = useState<Date>(new Date())
    // User-picked as-of date; null = not picked, fall back to the default (current business date)
    const [pickedAsOfDate, setPickedAsOfDate] = useState<Date | null>(null)
    const [deal, setDeal] = useState<DealParams>()
    const [lendersPositions, lendersPositionshandler] =
        useListState<LendersPosition>([])

    const [loading, setLoading] = useState<boolean>(false)

    const { data: facilityLendersPositions, isLoading: loadingFacilityLendersPositions } = useSelector(
        getFacilityLendersPositionsWithLoading
    )
    const { data: loanLendersPositions, isLoading: loadingLoanLendersPositions } = useSelector(
        getLoanLendersPositionsWithLoading
    )
    const facility = useSelector(getFacility)
    const deals = useSelector(getDeals)
    const loan = useSelector(getLoan)
    const loanAmendment = useSelector(getLoanAmendment)
    const currentDate = useSelector(getBusinessDate)
    const facilityAmendments: FacilityParams[] = useSelector(
        getFacilityAmendments
    )
    const entities = useSelector(getEntities)

    // Use amendment loan if it exists and has amendmentType AND we're viewing Draft or Submitted status
    // Otherwise use the regular loan to ensure we show the right data based on URL status
    const activeLoan = ((status === 'Draft' || status === 'Submitted') && loanAmendment && loanAmendment.amendmentType) ? loanAmendment : loan

    // Track previous values to detect when we need to refresh
    const prevContractIdRef = useRef<string>()
    const prevStatusRef = useRef<string>()
    const prevLoanLendersPositionsRef = useRef<LendersPosition[]>()

    // Clear lender positions when contractId or status changes to prevent caching issues
    useEffect(() => {
        const contractIdChanged = prevContractIdRef.current !== contractId
        const statusChanged = prevStatusRef.current !== status

        if (contractIdChanged || statusChanged) {
            lendersPositionshandler.setState([])
            // A picked as-of date belongs to the contract it was picked on
            setPickedAsOfDate(null)
        }

        prevContractIdRef.current = contractId
        prevStatusRef.current = status
        prevLoanLendersPositionsRef.current = loanLendersPositions
    }, [contractId, status, loanLendersPositions])

    useEffect(() => {
        dispatch(loadEntities())
        dispatch(loadContacts())
        dispatch(loadWireInstructions())
        dispatch(loadAllDeals())
        dispatch(loadIndexRateOptions())
        dispatch(loadAllFacilities())
        dispatch(loadCalendars())
        dispatch(loadAllEntityProfiles())
    }, [])

    useEffect(() => {
        // For Draft/Submitted amendments with an amendmentDate, use that date as the as-of date
        // so lender positions are shown as of the amendment's effective date (may be backdated).
        // Otherwise default to max(contract start date, current business date): the
        // business date for every started contract, but never before the contract
        // starts — a date before the first proRata would show all positions as 0.
        // The user can override via the picker.
        if (contractType === 'loans' && (activeLoan?.status === 'Draft' || activeLoan?.status === 'Submitted') && activeLoan?.amendmentType && activeLoan?.amendmentDate) {
            setEffectiveDate(stringToDate(activeLoan.amendmentDate))
            return
        }
        const contractStartDate = stringToDate((contractType === 'loans' ? activeLoan?.startDate : facility?.startDate) ?? '')
        setEffectiveDate(contractStartDate > currentDate ? contractStartDate : currentDate)
    }, [facility, activeLoan, currentDate, contractType])

    useEffect(() => {
        if (contractType === 'loans') {

            // If this is a draft or submitted amendment (has amendmentType) with amendmentLenders, use those instead of ledger positions
            if ((activeLoan?.status === 'Draft' || activeLoan?.status === 'Submitted') && activeLoan?.amendmentType && activeLoan?.amendmentLenders && activeLoan.amendmentLenders.length > 0) {

                // Calculate total amount for percentage calculation
                const totalAmount = activeLoan.amendmentLenders.reduce((sum: number, al: any) => sum + Number(al.amount || 0), 0)

                // For amendments, we need to get wire instructions and contacts from the approved loan's lender positions
                // The approved loan positions are in loanLendersPositions
                const approvedPositionsMap = new Map<string, LendersPosition>()
                if (loanLendersPositions && loanLendersPositions.length > 0) {
                    loanLendersPositions.forEach(lp => {
                        approvedPositionsMap.set(lp.lender.id, lp)
                    })
                }

                // Convert amendmentLenders to LendersPosition format
                // Use the amendment's amendmentDate for proRata effectiveDate so getAmount/getPercentage
                // returns positions as of the amendment's date (handles backdated amendments).
                // Fall back to today only if amendmentDate is absent (LoanEdit case).
                const amendmentDateStr = activeLoan.amendmentDate ?? new Date().toISOString().split('T')[0]

                const amendmentPositions: LendersPosition[] = activeLoan.amendmentLenders.map((al: any) => {
                    // Get the corresponding approved position for this lender
                    const approvedPosition = approvedPositionsMap.get(al.lender.id)

                    // Calculate percentage: (lender amount / total amount) * 100
                    const percentage = totalAmount > 0 ? ((Number(al.amount) / totalAmount) * 100).toFixed(6) : '0'

                    return {
                        lender: al.lender,
                        amount: al.amount,
                        percentage: percentage,
                        status: activeLoan.status, // Use actual status (Draft or Submitted)
                        effectiveDate: amendmentDateStr,
                        referenceId: al.id || '',
                        // Use wire instructions and contacts from the approved position if available
                        wireInstructions: approvedPosition?.wireInstructions || al.wireInstructions || [],
                        adminContactId: approvedPosition?.adminContactId || al.adminContactId || null,
                        creditContactId: approvedPosition?.creditContactId || al.creditContactId || null,
                        proRatas: [{
                            amount: al.amount,
                            percentage: percentage,
                            effectiveDate: amendmentDateStr, // Use amendmentDate so getAmount/getPercentage can find it
                            transactionType: 'Amendment' as any,
                            interestEarned: undefined,
                            regularInterestProcessed: undefined,
                            totalAmount: al.amount,
                            loanData: undefined,
                        }],
                    }
                })
                lendersPositionshandler.setState(amendmentPositions)
                return
            }

            // For non-draft loans, use ledger positions and extract latest amounts from proRatas
            if (!loanLendersPositions) {
                return
            }

            // Process positions to use the latest proRata amounts
            const processedPositions = loanLendersPositions.map((lp: LendersPosition) => {
                if (!lp.proRatas || lp.proRatas.length === 0) {
                    return lp
                }

                const isLocked = activeLoan?.status === 'Locked'
                const targetProRata = lp.proRatas[lp.proRatas.length - 1]

                return {
                    ...lp,
                    amount: targetProRata.amount,
                    percentage: targetProRata.percentage,
                    status: isLocked ? 'Locked' : 'Approved',
                    effectiveDate: targetProRata.effectiveDate,
                }
            })

            lendersPositionshandler.setState(processedPositions)
            return
        }
        if (contractType === 'facilities') {
            if (!facilityLendersPositions) {
                return
            }
            lendersPositionshandler.setState(facilityLendersPositions)
            return
        }
        lendersPositionshandler.setState([])
    }, [facilityLendersPositions, loanLendersPositions, contractType, activeLoan, contractId, status, loanAmendment, loan])
    useEffect(() => {
        if (contractType === 'loans') {
            setLoading(loadingLoanLendersPositions)
        }
        if (contractType === 'facilities') {
            setLoading(loadingFacilityLendersPositions)
        }
    }, [loadingFacilityLendersPositions, loadingLoanLendersPositions, contractType])

    useEffect(() => {
        if (contractType === 'loans') {
            // For Draft or Submitted status, try to load as amendment first (in case it's an amendment)
            // Also load as regular loan as fallback
            // The activeLoan variable will select the right one based on amendmentType
            if (status === 'Draft' || status === 'Submitted') {
                // Try loading as amendment (will fail silently if it's not an amendment)
                dispatch(loadOneLoanAmendment(contractId ?? '', status ?? ''))
                // Also load as regular loan
                dispatch(loadOneLoan(contractId ?? '', status ?? ''))
                // Load lender positions
                dispatch(loadLendersPositionsByLoan(contractId ?? ''))
            } else {
                // For non-draft status, only load as regular loan
                dispatch(loadOneLoan(contractId ?? '', status ?? ''))
                dispatch(loadLendersPositionsByLoan(contractId ?? ''))
            }
            return
        }
        if (contractType === 'facilities') {
            dispatch(loadAllFacilityAmendments())
            dispatch(loadLendersPositionsByFacility(contractId ?? ''))
            dispatch(loadOneFacility(contractId ?? '', status ?? ''))
            dispatch(loadOneFacilityAmounts(contractId ?? ''))
            return
        }
    }, [contractId, contractType, status])

    useEffect(() => {
        if (!facilityAmendments || contractType !== 'facilities') {
            return
        }
        const amended_facility = facilityAmendments.find(
            amendment => amendment.id === contractId
        )
        if (amended_facility) {
            dispatch(setOneFacility(amended_facility))
        }
    }, [facility, facilityAmendments])

    useEffect(() => {
        if (!activeLoan || contractType !== 'loans') {
            return
        }
        // Safely access facilityId - it should exist after loadOneLoan properly extracts the nested loan
        const facilityId = activeLoan.facilityId?.id
        if (!facilityId) {
            console.error('Loan missing facilityId:', activeLoan)
            return
        }
        if (activeLoan.status === 'Locked') {
            dispatch(loadOneFacility(facilityId, 'Locked'))
        }
        dispatch(loadOneFacility(facilityId, 'Approved'))
    }, [activeLoan, contractType])

    useEffect(() => {
        if (!facility || !deals) {
            return
        }
        const selectedDeal = deals.find(
            (deal: any) => deal.id === facility.dealId.id
        )
        if (!selectedDeal) {
            return
        }
        dispatch(setOneDeal(selectedDeal))
        setDeal(selectedDeal)
    }, [facility, deals])

    // get lender name, mei, fund manager info based on the lender id in the lendersPositions data
    const lendersWithEntityNames = lendersPositions.map(position => {
        const entity = entities.find(entity => entity.id === position.lender.id)
        return {
            ...position,
            entityName: entity?.entityName || '',
            mei: entity?.mei || '',
            fundManagerName: entity?.fundManager || '',
            fundManagerMei: entity?.fundManagerMei || '',
        }
    })


    const filterPositions = (positions: LendersPositionWithEntityNames[], term: string[]) => {
        if (term.length === 0) return positions
        return positions.filter(pos =>
            term.some(t =>
                pos.lender.id.includes(t) ||
                pos.entityName?.toLowerCase().includes(t.toLowerCase()) ||
                pos.mei?.toLowerCase().includes(t.toLowerCase()) ||
                pos.fundManagerName?.toLowerCase().includes(t.toLowerCase()) ||
                pos.fundManagerMei?.toLowerCase().includes(t.toLowerCase())
            )
        )
    }

    const filteredLendersPositions = filterPositions(lendersWithEntityNames, searchTerms)

    const tabs = {
        lenderAllocationsFund: {
            route: `/dealmanagement/${contractType}/${contractId}/${status}/lenders`,
            label: 'FUND',
        },
        lenderAllocationsFundManager: {
            route: `/dealmanagement/${contractType}/${contractId}/${status}/lenders/fund-manager`,
            label: 'FUND MANAGER',
        },
    }

    // While an amendment is being drafted/reviewed, positions are pinned to the
    // amendment's effective date — a user-picked as-of date must not override it
    // (the picker is hidden then, but a date picked earlier could linger).
    const isAmendmentActive = contractType === 'facilities'
        ? !!facility?.amendmentType
        : !!activeLoan?.amendmentType

    const outletContext: LenderAllocationsContext = {
        effectiveDate: isAmendmentActive
            ? effectiveDate
            : pickedAsOfDate ?? effectiveDate,
        currency: contractType === 'facilities'
            ? facility?.baseCurrency ?? 'USD'
            : activeLoan?.currency ?? 'USD',
        lendersPositions: filteredLendersPositions,
        loading,
        contractType,
        status,
        isAmendment: !!activeLoan?.amendmentType,
    }

    const items = [
        { title: 'Deal Management', href: '/dealmanagement' },
        {
            title: deal?.dealName,
            href: `/dealmanagement/deal/${deal?.id}/${status}`,
        },
        { title: facility?.name ?? 'Facility', href: `/dealmanagement/facilities/${facility?.id}/${facility?.status}` },
        ...(contractType === 'loans'
            ? [{ title: 'Loans', href: `/dealmanagement/facilities/${facility?.id}/${facility?.status}/loans` }]
            : []),
    ].map((item, index) => (
        <Anchor
            onClick={item.href ? () => navigate(item.href) : () => null}
            key={index}
            color='#374151'
            fw={500}
            fz='14px'
        >
            {item.title}
        </Anchor>
    ))

    // Prepare lender data for multiselect options in the SearchInput component. Pipe symbol will allow label to be split into 2 columns
    const lenderOptions = lendersWithEntityNames.map(lender => ({
        label: `${lender.entityName}|${lender.mei}`,
        value: lender.lender.id ?? '',
    }))

    // Prepare fund manager data for multiselect options in the SearchInput component - this eliminate duplicate fund manager options
    // Pipe symbol will allow label to be split into 2 columns when used in the MultiSelect component
    const fundManagerOptions = Array.from(
        new Map(
            lendersWithEntityNames.map(fundManager => {
                const label = `${fundManager.fundManagerName}|${fundManager.fundManagerMei}`
                const value = fundManager.fundManagerMei ?? ''
                return [label + value, { label, value }]
            })
        ).values()
    )

    // Dynamically set options based on the selected tab (derived from URL)
    const searchOptions = isFundManagerTab
        ? [{ label: 'Entity Name|MEI', value: '', isHeader: true, disabled: true }, ...fundManagerOptions]
        : [{ label: 'Entity Name|MEI', value: '', isHeader: true, disabled: true }, ...lenderOptions]

    // null (cleared) falls back to the default effective date
    const handleAsOfDateChange = (value: Date | null) => {
        setPickedAsOfDate(value)
    }


    return (
        <Stack w='100%'>
            <Breadcrumbs
                separator={<IonIcon name='chevron-forward-outline' />}
            >
                {items}
            </Breadcrumbs>
            <LenderAllocationsHeader />
            <Group w='100%' position='apart' className='tabsGapsActivity'>
                <Group position='right'>
                    <SegmentControlUpdated tabs={tabs} />
                </Group>
                <Group noWrap>
                    {contractType == 'facilities' && facility?.status === 'Draft' && (
                        <PrimaryInput
                            className='activityButton'
                            text='Edit Lender Allocations'
                            onClick={() => setOpenEdit(true)}
                        />
                    )}
                </Group>
            </Group>
            <MultiSelectSearchInput
                placeholder='Search by Entity Name or MEI'
                value={searchTerms}
                onChange={setSearchTerms}
                data={searchOptions}
                leftSection={!isAmendmentActive ? (
                    <DateInput
                        className='lender-input-search-date'
                        value={pickedAsOfDate}
                        onChange={handleAsOfDateChange}
                        placeholder={`Select date for amounts at a specific moment (default: ${formatDateToShow(effectiveDate)})`}
                        clearable
                        valueFormat='DD-MMM-YYYY'
                        firstDayOfWeek={0}
                        popoverProps={{ withinPortal: true }}
                        getDayProps={highlightToday()}
                    />
                ) : undefined}
            />
            <Group noWrap w='100%' mt='10px'>
                <Outlet context={outletContext} />
            </Group>
            {contractType == 'facilities' && facility?.status === 'Draft' && (
                <Modal
                    className='modal-body modal-body-template fit-content-modal'
                    opened={openEdit}
                    onClose={() => setOpenEdit(false)}
                    centered={true}
                    size='77%'
                >
                    <UpdatedFacilitiesScreen
                        amendment={facility.amendmentType ? 'amend' : null}
                        deal={deal}
                        facility={facility}
                        onClose={() => setOpenEdit(false)}
                        step={1}
                    />
                </Modal>
            )}
        </Stack>
    )
}


export function LenderAllocationsFundPanel() {
    const ctx = useLenderAllocationsContext()
    return (
        <LenderAllocationTable
            effectiveDate={ctx.effectiveDate}
            currency={ctx.currency}
            lendersPositions={ctx.lendersPositions}
            loading={ctx.loading}
            contractType={ctx.contractType}
            status={ctx.status}
            isAmendment={ctx.isAmendment}
        />
    )
}


export function LenderAllocationsFundManagerPanel() {
    const ctx = useLenderAllocationsContext()
    return (
        <FundManagerLenderTable
            effectiveDate={ctx.effectiveDate}
            currency={ctx.currency}
            lendersPositions={ctx.lendersPositions}
            loading={ctx.loading}
        />
    )
}